import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseApiService } from './base-api.service';
import { NavalMatrizItem, CommissionReportsData } from '../interfaces/commission-reports.interface';
import { CommissionReportsApiResponse, NavalMatrizApiResponse } from '../interfaces/commission-reports-api.interface';

@Injectable({
  providedIn: 'root'
})
export class CommissionReportsService extends BaseApiService {

  /**
   * Obtém dados da Matriz de Comissão ("Batalha Naval")
   * @param exercicio Formato AAAAMM (ex: 202510). Se não fornecido, backend retorna o mais recente
   * @param dn Dealer Number (obrigatório)
   */
  getNavalMatriz(exercicio: number | undefined, dn: number): Observable<NavalMatrizItem[]> {
    if (!dn) {
      throw new Error('Parâmetro obrigatório: dn deve ser fornecido');
    }

    // Se exercicio não for fornecido, não enviar o parâmetro (backend retorna o mais recente)
    const exercicioParam = exercicio ? `exercicio=${exercicio}` : '';
    const endpoint = `/reports/commission/naval-matriz?dn=${dn}&${exercicioParam}`;
    if (this.configService.isDebugEnabled()) {
      console.log('🔵 API 2 - Chamando:', endpoint);
      console.log('🔵 API 2 - Parâmetros:', { exercicio: exercicio || 'não fornecido (mais recente)', dn });
    }

    interface NavalMatrizResponse {
      success: boolean;
      totalRecords: number;
      filters: {
        dn: number;
        exercicio: string | null;
      };
      data: NavalMatrizApiResponse[];
      timestamp: string;
    }

    return this.get<NavalMatrizResponse>(endpoint).pipe(
      map(response => {
        const apiData = response.data || [];
        if (this.configService.isDebugEnabled()) {
          console.log('📦 Resposta bruta da API 2 (naval-matriz):', apiData);
        }

        // Verificar se os dados são válidos
        if (!Array.isArray(apiData) || apiData.length === 0) {
          console.error('❌ API 2 retornou array vazio ou inválido');
          return [];
        }

        // Verificar se o primeiro item tem a estrutura esperada
        const primeiroItem = apiData[0];
        if (!primeiroItem || !primeiroItem.Exercicio || !primeiroItem.Tipo) {
          console.error('❌ API 2 retornou dados com estrutura inválida:', primeiroItem);
          console.error('❌ Esperado: { DN, Exercicio, Tipo, Penetration, ... }');
          return [];
        }

        return apiData.map(item => this.mapNavalMatrizItem(item));
      })
    );
  }

  /**
   * Obtém dados agrupados da Matriz de Comissão ("Batalha Naval")
   * @param dn Dealer Number (obrigatório)
   * @returns Observable<string> - Retorna uma string com os dados agrupados (text/plain; charset=utf-8)
   */
  getNavalMatrizGroup(dn: number): Observable<string> {
    if (!dn) {
      throw new Error('Parâmetro obrigatório: dn deve ser fornecido');
    }

    const endpoint = `/reports/commission/naval-matriz/group?dn=${dn}`;
    const url = `${this.getBaseUrl()}${endpoint}`;
    if (this.configService.isDebugEnabled()) {
      console.log('🟣 API 3 - Chamando:', endpoint);
      console.log('🟣 API 3 - Parâmetros:', { dn });
    }

    // Faz requisição com responseType: 'text' para receber texto puro
    return this.getHttpClient().get(url, {
      responseType: 'text',
      headers: this.getAuthHeaders().set('Accept', 'text/plain; charset=utf-8'),
    });
  }

  /**
   * Mapeia dados da API para o formato interno
   */
  private mapNavalMatrizItem(apiItem: NavalMatrizApiResponse): NavalMatrizItem {
    return {
      idIndicadorNaval: 0, // Não vem na API
      dn: apiItem.DN,
      exercicio: parseInt(apiItem.Exercicio, 10),
      tipo: apiItem.Tipo,
      penetration: apiItem.Penetration,
      faixa_0_39_9: apiItem['0-39,9'],
      faixa_40_49_9: apiItem['40-49,9'],
      faixa_50_54_9: apiItem['50-54,9'],
      faixa_55_59_9: apiItem['55-59,9'],
      faixa_60_64_9: apiItem['60-64,9'],
      faixa_65_69_9: apiItem['65-69,9'],
      faixa_70_74_9: apiItem['70-74,9'],
      faixa_75_79_9: apiItem['75-79,9'],
      faixa_80_84_9: apiItem['80-84,9'],
      faixa_85_89_9: apiItem['85-89,9'],
      faixa_90_99_9: apiItem['90-99,9'],
      faixa_100_124_9: apiItem['100-124,9'],
      faixa_125_149_9: apiItem['125-149,9'],
      faixa_150_174_9: apiItem['150-174,9'],
      faixa_175_199_9: apiItem['175-199,9'],
      faixa_Maior_200: apiItem['>200'],
      dtImportacao: apiItem.DT_IMPORTACAO || ''
    };
  }

  /**
   * Obtém dados gerais e históricos de comissões
   * @param exercicio Formato AAAAMM (ex: 202510). OBRIGATÓRIO - API retorna 404 se não fornecido
   * @param dn Dealer Number (obrigatório)
   */
  getCommissionReports(exercicio: number, dn: number): Observable<{ data: CommissionReportsData; availableExercicios: string[] }> {
    if (!dn) {
      throw new Error('Parâmetro obrigatório: dn deve ser fornecido');
    }

    if (!exercicio) {
      throw new Error('Parâmetro obrigatório: exercicio deve ser fornecido (API 1 não aceita sem exercicio)');
    }

    const endpoint = `/reports/commission?dn=${dn}&exerFech=${exercicio}`;
    if (this.configService.isDebugEnabled()) {
      console.log('🟢 API 1 - Chamando:', endpoint);
      console.log('🟢 API 1 - Parâmetros:', { exercicio, dn });
    }

    interface CommissionReportsResponse {
      success: boolean;
      totalRecords: number;
      filters: {
        dn: number;
        exercicio: string | null;
        availableExercicios: string[];
      };
      data: CommissionReportsApiResponse[];
      timestamp: string;
    }

    return this.get<CommissionReportsResponse>(endpoint).pipe(
      map(response => {
        // API retorna ResponseTemplate com data array, pegamos o primeiro item
        const apiData = Array.isArray(response.data) ? response.data[0] : response.data;
        return {
          data: this.mapCommissionReportsData(apiData),
          availableExercicios: response.filters?.availableExercicios || []
        };
      })
    );
  }

  /**
   * Mapeia dados da API para o formato interno
   */
private mapCommissionReportsData(apiData: CommissionReportsApiResponse): CommissionReportsData {
  return {
    idIndicador: 0,
    exercicio: parseInt(apiData.Exercicio, 10),
    dn: apiData.DN,
        exerFech: apiData.Exer_Fech,
    exerFechAnt: apiData.Exer_Fech_Ant,
    nmGrpComission: apiData.NM_GRP_COMISSION,
    nmRegional: apiData.NM_REGIONAL,
    dsCustomCategoriaPai: apiData.DS_CUSTOM_CATEGORIA_PAI,
    indiceVigenteVarejo: apiData.INDICE_VIGENTE_VAREJO,
    indiceVigenteFrota: apiData.INDICE_VIGENTE_FROTA,
    indiceParaProximoVarejo: apiData.INDICE_PARA_PROXIMO_VAREJO,
    indiceParaProximoFrota: apiData.INDICE_PARA_PROXIMO_FROTA,
    nomeMes1: apiData.NOME_MES_1,
    nomeMes2: apiData.NOME_MES_2,
    nomeMes3: apiData.NOME_MES_3,
    nomeMes4: apiData.NOME_MES_4,
    nomeMes5: apiData.NOME_MES_5,
    nomeMes6: apiData.NOME_MES_6,
    penMes1Varejo: apiData.PEN_MES_1_VAREJO,
    isrMes1Varejo: apiData.ISR_MES_1_VAREJO,
    penMes2Varejo: apiData.PEN_MES_2_VAREJO,
    isrMes2Varejo: apiData.ISR_MES_2_VAREJO,
    penMes3Varejo: apiData.PEN_MES_3_VAREJO,
    isrMes3Varejo: apiData.ISR_MES_3_VAREJO,
    penMes4Varejo: apiData.PEN_MES_4_VAREJO,
    isrMes4Varejo: apiData.ISR_MES_4_VAREJO,
    penMes5Varejo: apiData.PEN_MES_5_VAREJO,
    isrMes5Varejo: apiData.ISR_MES_5_VAREJO,
    penMes6Varejo: apiData.PEN_MES_6_VAREJO,
    isrMes6Varejo: apiData.ISR_MES_6_VAREJO,
    penMes1Frota: apiData.PEN_MES_1_FROTA,
    isrMes1Frota: apiData.ISR_MES_1_FROTA,
    penMes2Frota: apiData.PEN_MES_2_FROTA,
    isrMes2Frota: apiData.ISR_MES_2_FROTA,
    penMes3Frota: apiData.PEN_MES_3_FROTA,
    isrMes3Frota: apiData.ISR_MES_3_FROTA,
    penMes4Frota: apiData.PEN_MES_4_FROTA,
    isrMes4Frota: apiData.ISR_MES_4_FROTA,
    penMes5Frota: apiData.PEN_MES_5_FROTA,
    isrMes5Frota: apiData.ISR_MES_5_FROTA,
    penMes6Frota: apiData.PEN_MES_6_FROTA,
    isrMes6Frota: apiData.ISR_MES_6_FROTA,
    periodoAtual: apiData.PERIODO_ATUAL,
    penetrationVarejo: apiData.PENETRATION_VAREJO,
    penetrationVarejoAnt: apiData.PENETRATION_VAREJO_ANT, // ADICIONADO
    isrVarejo: apiData.ISR_VAREJO,
    isrVarejoAnt: apiData.ISR_VAREJO_ANT, // ADICIONADO
    isrVarejoBra: apiData.ISR_VAREJO_BRA, // ADICIONADO
    penVarejoBra: apiData.PEN_VAREJO_BRA,
    penVarejoReg: apiData.PEN_VAREJO_REG,
    penVarejoDn: apiData.PEN_VAREJO_DN,
    isrVarejoReg: apiData.ISR_VAREJO_REG,
    isrVarejoDn: apiData.ISR_VAREJO_DN,
    penetrationFrota: apiData.PENETRATION_FROTA,
    penetrationFrotaAnt: apiData.PENETRATION_FROTA_ANT, // ADICIONADO
    isrFrota: apiData.ISR_FROTA,
    isrFrotaAnt: apiData.ISR_FROTA_ANT, // ADICIONADO
    isrFrotaBrasil: apiData.ISR_FROTA_BRASIL, // ADICIONADO (note o nome)
    penFrotaBrasil: apiData.PEN_FROTA_BRASIL,
    penFrotaReg: apiData.PEN_FROTA_REG,
    penFrotaDn: apiData.PEN_FROTA_DN,
    isrFrotaReg: apiData.ISR_FROTA_REG,
    isrFrotaDn: apiData.ISR_FROTA_DN,
    comissMensalReg: apiData.COMISS_MENSAL_REG,
    comissMensalNac: apiData.COMISS_MENSAL_NAC,
  };
}
}

